import React, { useCallback, useMemo, useState, type ReactElement } from 'react';
import { PlainTable } from '@/components/ui';
import type { UnifiedAuditRow } from '@/api/console';
import { AuditDetailDrawer } from './AuditDetailDrawer';
import type {
    AuditFacetOptions,
    AuditFilters,
    AuditSort,
    AuditSortField,
} from './auditFilterTypes';
import { useAuditColumns } from './useAuditColumns';
import styles from './AuditTable.module.css';

/**
 * EAA-1821: the Nhật ký table — the six columns from `useAuditColumns` rendered
 * through `packages/ui`'s `PlainTable`, plus the detail drawer a row opens.
 *
 * The `<table>`, its scroll region, the sticky header, `aria-sort` and the
 * row-activation keyboard handling are all `PlainTable`'s. This screen used to
 * hand-roll every one of them; that scaffolding is not about auditing, and the
 * next console screen that cannot use `DataTable` would have hand-rolled it
 * again. What is left here is the two things that ARE about auditing: which row
 * is selected, and the drawer that shows it.
 *
 * `DataTable` (antd) still does not fit: a column that swaps identity with the
 * data source, a failed-row tint keyed off `row.success`, and filter popovers
 * anchored inside the scroll region are all outside what `ColumnConfig` can say.
 * That is precisely the case `PlainTable` exists for — see its docstring.
 *
 * A DUMB renderer either way: it owns no filter state and issues no request.
 */

export interface AuditTableProps {
    rows: UnifiedAuditRow[];
    /**
     * Account source: column 5 is "Chế độ" over `credential_mode`. Admin source:
     * the same slot becomes the target-type category.
     */
    showCredentialMode: boolean;
    sort: AuditSort;
    onSortChange: (field: AuditSortField) => void;
    filters: AuditFilters;
    onFiltersChange: (next: AuditFilters) => void;
    options: AuditFacetOptions;
}

function rowKey(row: UnifiedAuditRow): string {
    return `${row.kind}-${row.id}`;
}

// EAA-1821: the tint is a fact about an audit outcome, so it stays here as a
// class the app's own stylesheet owns — `PlainTable` takes a `rowClassName`
// callback precisely so the kit never has to learn what "failed" means.
function rowClassName(row: UnifiedAuditRow): string {
    return row.success ? styles.row : `${styles.row} ${styles.rowFailed}`;
}

function AuditTableBase({
    rows,
    showCredentialMode,
    sort,
    onSortChange,
    filters,
    onFiltersChange,
    options,
}: AuditTableProps): ReactElement {
    const [selectedRow, setSelectedRow] = useState<UnifiedAuditRow | null>(null);

    const columns = useAuditColumns({
        showCredentialMode,
        filters,
        onFiltersChange,
        options,
    });

    // EAA-1821: the sortable columns are keyed by the backend's own `ordering`
    // field names, so the key coming back from the header IS an `AuditSortField`
    // and needs no lookup table. The panel, not the table, decides how a click
    // flips the direction.
    const handleSort = useCallback(
        (key: string) => onSortChange(key as AuditSortField),
        [onSortChange]
    );

    const sortModel = useMemo(
        () => ({ key: sort.field, direction: sort.direction, onSort: handleSort }),
        [sort.field, sort.direction, handleSort]
    );

    const handleRowActivate = useCallback((row: UnifiedAuditRow) => setSelectedRow(row), []);
    const handleDrawerClose = useCallback(() => setSelectedRow(null), []);

    return (
        <>
            <PlainTable
                // The class lands on `PlainTable`'s ROOT, which IS the scroll
                // region — that is why this is the `-scroll` class and the table
                // inside it is reached through the kit's `ui-plain-table` hook.
                className={styles.tableScroll}
                columns={columns}
                rows={rows}
                rowKey={rowKey}
                sort={sortModel}
                // The ROW is the affordance that opens the drawer — the design has
                // six columns and no "Thao tác" column, and nothing inside a row
                // competes for the click.
                onRowActivate={handleRowActivate}
                rowClassName={rowClassName}
                stickyHeader
            />
            <AuditDetailDrawer row={selectedRow} onClose={handleDrawerClose} />
        </>
    );
}

/**
 * EAA-1821: memoized — the panel above re-renders on every keystroke in the
 * filter bar's search box, and re-rendering a 50-row table (each row carrying
 * six cells and the header six popover triggers) for an unchanged `rows` array
 * is the difference between a search box that types smoothly and one that
 * stutters. Callers must pass stable `rows`/`options`/`filters` references.
 */
export const AuditTable = React.memo(AuditTableBase);
